/**
 * Role predicates and the role -> home-route mapping (P4.6-C).
 *
 * ============================================================================
 * WHY THIS IS NOT IN auth.ts
 * ============================================================================
 * `auth.ts` THROWS AT MODULE SCOPE when `VITE_COGNITO_USER_POOL_ID` /
 * `VITE_COGNITO_USER_POOL_CLIENT_ID` are unset (it constructs a
 * `CognitoUserPool` eagerly). That is deliberate and correct for the auth
 * module — a portal build with no pool configured is broken and should say so
 * loudly at boot rather than at first sign-in.
 *
 * But it makes `auth.ts` a module that cannot be imported for its VALUES from
 * anywhere that doesn't already need Cognito. Today nothing does: every page
 * and context imports `AuthenticatedUser` and friends with `import type`,
 * which `verbatimModuleSyntax` erases entirely, so `auth.ts` never enters a
 * page's module graph. Adding a runtime `isTenantUser()` to it and importing
 * that from `LeadDetailPage.tsx` broke exactly that property — four
 * LeadDetailPage test suites failed to even load, because `src/test/
 * setupEnv.ts` stubs `VITE_API_BASE_URL` but not the Cognito pair (it never
 * had to).
 *
 * The fix is this module rather than widening the test setup's env stubs. Two
 * reasons, and the second is the load-bearing one:
 *   - Broadening `setupEnv.ts` would make every future page test transitively
 *     construct a real `CognitoUserPool`, for no benefit.
 *   - A predicate over a plain discriminated union has no business living in
 *     the Cognito SDK wrapper anyway. It is a fact about the SHAPE, not about
 *     the identity provider — so it belongs somewhere any page can import
 *     without consequence. This module is deliberately side-effect-free and
 *     must stay that way.
 *
 * The type-only import below is erased at compile time, so `auth.ts` importing
 * `ORG_ADMIN_ROLE` from here creates no runtime cycle.
 */
import type { AuthenticatedUser, TenantUser, OrgAdminUser } from "./auth";

/**
 * The `custom:role` claim value that selects the org-scoping level. One
 * constant so the string is never spelled twice on the client, mirroring
 * DockPro-Backend's own `ORG_ADMIN_ROLE` in `lib/lambda/utils/portalRequest.ts`
 * — the refusal there (`getPortalContext` throws for it) and the gate there
 * (`requireOrgAdmin` accepts only it) must agree on this literal, and so must
 * this client.
 */
export const ORG_ADMIN_ROLE = "orgAdmin";

/** True for an owner/rep — i.e. anything that has a `tenantId` to act on. */
export function isTenantUser(user: AuthenticatedUser): user is TenantUser {
  return user.role !== ORG_ADMIN_ROLE;
}

/** True for an org admin — i.e. an `orgId`-scoped identity with no tenant. */
export function isOrgAdmin(user: AuthenticatedUser): user is OrgAdminUser {
  return user.role === ORG_ADMIN_ROLE;
}

/** Where `/organization` lives. One constant, since LoginPage, ProtectedRoute
 * and main.tsx all have to agree on it. */
export const ORG_ADMIN_HOME = "/organization";

/** The tenant portal's own default landing route, unchanged from before this
 * round — every owner/rep still lands here. */
export const TENANT_HOME = "/leads";

/**
 * The route a freshly-signed-in user belongs on.
 *
 * An org admin CANNOT use `/leads`: their token carries no `tenantId`, so
 * `GET /api/portal/leads` would be refused (DockPro-Backend's
 * `getPortalContext` throws for an orgAdmin context, which API Gateway
 * surfaces as a 502). Sending them to `/leads` and letting a redirect bounce
 * them back would flash a broken page on every sign-in, so the destination is
 * decided up front from the role that is already in hand.
 */
export function homePathForUser(user: AuthenticatedUser): string {
  return isOrgAdmin(user) ? ORG_ADMIN_HOME : TENANT_HOME;
}
